import React from 'react';
import { StepJumpControlStep } from './StepJumpControl';

const HOOK_STAGES = ['why', 'define', 'do', 'explain', 'apply', 'reflect'] as const;

interface HookProgressRailProps {
  steps: StepJumpControlStep[];
  currentStepId: number | null;
  stepNavLoadingId: number | null;
  onSelectStep: (step: StepJumpControlStep) => void;
  onOpenStepJump: () => void;
}

const stageLabel = (stage: string): string => stage.toUpperCase();

export const HookProgressRail: React.FC<HookProgressRailProps> = ({
  steps,
  currentStepId,
  stepNavLoadingId,
  onSelectStep,
  onOpenStepJump,
}) => {
  const currentStep = steps.find((step) => step.id === currentStepId) || null;
  const currentStage = String(currentStep?.stage_tag || '').trim().toLowerCase();
  const currentStageIndex = HOOK_STAGES.indexOf(currentStage as typeof HOOK_STAGES[number]);

  return (
    <section
      aria-label="Hook progression"
      className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm"
    >
      <div className="flex flex-wrap items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500">Hook progression</p>
        <button
          type="button"
          className="rounded-lg border border-slate-200 bg-white px-3 py-1.5 text-xs font-medium text-slate-700 hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
          onClick={onOpenStepJump}
        >
          Jump to any step
        </button>
      </div>
      <div className="mt-3 grid grid-cols-2 gap-2 sm:grid-cols-3 xl:grid-cols-6">
        {HOOK_STAGES.map((stage, stageIndex) => {
          const stageSteps = steps.filter((step) => String(step.stage_tag || '').trim().toLowerCase() === stage);
          const isCurrent = currentStage === stage;
          const isComplete = currentStageIndex >= 0 && stageIndex < currentStageIndex;
          const statusLabel = isCurrent ? 'Current' : isComplete ? 'Complete' : stageSteps.length > 0 ? 'Upcoming' : 'Not configured';
          const statusMark = isCurrent ? '●' : isComplete ? '✓' : '○';

          return (
            <details
              key={`hook-stage-${stage}`}
              className={`group rounded-lg border ${
                isCurrent
                  ? 'border-indigo-400 bg-indigo-50'
                  : isComplete
                    ? 'border-emerald-200 bg-emerald-50'
                    : 'border-slate-200 bg-slate-50'
              }`}
            >
              <summary
                className="list-none cursor-pointer rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                aria-current={isCurrent ? 'step' : undefined}
              >
                <span className="flex items-center justify-between gap-2">
                  <span className={`text-sm font-semibold ${isCurrent ? 'text-indigo-900' : isComplete ? 'text-emerald-900' : 'text-slate-700'}`}>
                    <span aria-hidden="true" className="mr-1.5">{statusMark}</span>
                    {stageLabel(stage)}
                  </span>
                  <span className="text-[11px] text-slate-500">{stageSteps.length || '—'}</span>
                </span>
                <span className="mt-1 block text-[11px] text-slate-500">{statusLabel}</span>
              </summary>
              {stageSteps.length > 0 ? (
                <div className="space-y-1 border-t border-current/10 p-2">
                  {stageSteps.map((step) => (
                    <button
                      key={`hook-stage-${stage}-step-${step.id}`}
                      type="button"
                      className={`w-full rounded px-2 py-1.5 text-left text-xs focus:outline-none focus:ring-2 focus:ring-indigo-500 ${
                        step.id === currentStepId
                          ? 'bg-indigo-100 font-semibold text-indigo-900'
                          : 'bg-white text-slate-700 hover:bg-slate-100'
                      }`}
                      onClick={() => onSelectStep(step)}
                      disabled={step.id === currentStepId || stepNavLoadingId !== null}
                    >
                      Step {step.order}: {step.title || 'Untitled step'}
                    </button>
                  ))}
                </div>
              ) : null}
            </details>
          );
        })}
      </div>
    </section>
  );
};
